/**
 * 監修医: who stands behind what the clinic publishes.
 *
 * A table was the wrong shape for this the same way it was wrong for personas.
 * A supervisor is a person, and the thing this screen is for - "who do I credit
 * on this article" - is answered by a face, a name and a line of credentials,
 * which is what a card shows and five columns of truncated text do not.
 *
 * The sheet shows the CARD ITSELF: the same `.bio` markup an article publishes,
 * rendered with the same stylesheet. What a supervisor looks like on the page is
 * the only question anybody has about this data, and everywhere else in the
 * product it was answered by generating an article and looking.
 */
import { confirmDialog } from '../components/AppDialog.tsx';
import { useId, useState } from 'react';
import { useLoaderData, useOutletContext, useRevalidator, useSearchParams } from 'react-router';
import { del, imageUrl, patch, post, type CatalogItem, type Session } from '../api.ts';
import { inScope, sessionScope, type FactPanelProps } from '../fact-panel.ts';
import { Card, Pill, StatusPill } from '../components/ui.tsx';
import { Modal, ModalForm } from '../components/Modal.tsx';
import { Field, FieldGroup } from '../components/EditPanel.tsx';
import { Select } from '../components/Select.tsx';
import { ImageUpload } from '../components/ImageUpload.tsx';
import { Textarea } from '../components/Textarea.tsx';
import { AddFromLink } from '../components/AddFromLink.tsx';
import { AnimatePresence, listItem, m } from '../components/motion.tsx';
import { SkeletonPeople, usePendingScreen } from '../components/Skeleton.tsx';

interface Supervisor extends CatalogItem {
  storeName?: string | null;
  name?: string;
  title?: string;
  qualifications?: string;
  careerYears?: number | null;
  profile?: string;
}

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

/** The blank form, so "new" and "reset" are the same thing. */
const EMPTY = {
  name: '',
  title: '',
  careerYears: '',
  qualifications: '',
  profile: '',
};

type Form = typeof EMPTY;

function formFor(supervisor: Supervisor): Form {
  return {
    name: text(supervisor.name),
    title: text(supervisor.title),
    careerYears: typeof supervisor.careerYears === 'number' ? String(supervisor.careerYears) : '',
    qualifications: text(supervisor.qualifications),
    profile: text(supervisor.profile),
  };
}

/**
 * The stand-in for a doctor with no photograph.
 *
 * Not an empty frame and not a hole in the layout: the family name's first
 * character on the brand's own tint, which is a shape the eye can tell from its
 * neighbours in a grid. The same stand-in is what an article publishes, so a
 * clinic that never uploads a photograph still gets a card rather than a card
 * with a piece missing.
 */
function Face({
  photoId,
  name,
  size,
  className,
}: {
  photoId: unknown;
  name: string;
  size: number;
  /** Put on whichever element is drawn, so the card styles one thing, not two. */
  className?: string;
}) {
  if (typeof photoId === 'string' && photoId !== '') {
    return (
      <img
        {...(className ? { className } : {})}
        src={imageUrl(photoId)}
        alt=""
        width={size}
        height={size}
      />
    );
  }
  return (
    <span
      className={`ptile__blank${className ? ` ${className}` : ''}`}
      style={{ width: size, height: size, fontSize: Math.round(size / 2.6) }}
      aria-hidden
    >
      {name.charAt(0) || '医'}
    </span>
  );
}

/** The credentials, in the order a reader checks them. Blank ones are dropped. */
function creditLines(supervisor: Supervisor): string[] {
  return [
    text(supervisor.title),
    text(supervisor.qualifications),
    typeof supervisor.careerYears === 'number'
      ? `美容医療経験年数：${supervisor.careerYears}年`
      : '',
  ].filter((line) => line !== '');
}

function SupervisorTile({
  supervisor,
  /** Its place in the grid, which is how long it waits before arriving. */
  index,
  onOpen,
}: {
  supervisor: Supervisor;
  index: number;
  onOpen: () => void;
}) {
  const name = text(supervisor.name) || '（氏名なし）';
  const facts = [
    text(supervisor.title),
    typeof supervisor.careerYears === 'number' ? `経験${supervisor.careerYears}年` : '',
  ].filter((fact) => fact !== '');

  return (
    <m.button
      className="ptile"
      type="button"
      // Opts out of the stylesheet's own entrance: see styles.css.
      data-motion
      layout
      {...listItem(index)}
      onClick={onOpen}
    >
      <span className="ptile__face">
        <Face photoId={supervisor.photoId} name={name} size={72} />
      </span>

      <span className="ptile__label">{name}</span>

      {facts.length > 0 && (
        <span className="ptile__facts">
          {facts.map((fact) => (
            <span key={fact} className="tag">
              {fact}
            </span>
          ))}
        </span>
      )}

      <span className="ptile__desc">
        {text(supervisor.qualifications) ||
          text(supervisor.profile) ||
          'まだ内容がありません。開いて追加してください。'}
      </span>

      <span className="ptile__meta">
        {supervisor.storeId === null ? (
          <Pill>クリニック共通</Pill>
        ) : (
          <Pill tone="muted">{supervisor.storeName ?? '店舗'}</Pill>
        )}
        <StatusPill status={supervisor.status} />
      </span>
    </m.button>
  );
}

/**
 * The supervisor as the card an article carries.
 *
 * Deliberately the same class names and the same stylesheet rules as the block
 * the editor inserts and the exporter publishes - `.bio`, `.bio__label`,
 * `.bio__name`, `.bio__meta`, `.bio__profile`. A separate "preview" styled to
 * look approximately like the real thing is a second thing to keep in step, and
 * the one that drifts is the one nobody is looking at.
 */
function SupervisorCard({ supervisor }: { supervisor: Supervisor }) {
  const name = text(supervisor.name) || '（氏名なし）';
  const profile = text(supervisor.profile);

  return (
    <div className="bio">
      <Face photoId={supervisor.photoId} name={name} size={84} className="bio__photo" />
      <p className="bio__label">この記事の監修医師</p>
      <p className="bio__name">{name}</p>
      {creditLines(supervisor).map((line) => (
        <p key={line} className="bio__meta">
          {line}
        </p>
      ))}
      {profile !== '' && <p className="bio__profile">{profile}</p>}
    </div>
  );
}

export default function Supervisors() {
  const { items } = useLoaderData<{ items: Supervisor[] }>();
  const session = useOutletContext<Session>();
  return <SupervisorsPanel items={items} base="" scope={sessionScope(session)} />;
}

/**
 * The screen itself, on the clinic's route and on the JMC clinic page alike.
 * See fact-panel.ts for the three things that differ between the two.
 */
export function SupervisorsPanel({ items: rows, base, scope, clinicId, lead }: FactPanelProps<Supervisor>) {
  const revalidator = useRevalidator();
  const { isClinicAdmin, stores: ownStores, activeStore } = scope;

  /* Where a new one goes. Empty is clinic-wide, which only a clinic admin may write. */
  const [newScope, setNewScope] = useState<string>(isClinicAdmin ? '' : (ownStores[0]?.id ?? ''));

  /* Same control, same URL parameter and same defaults as every other fact screen. */
  const [params, setParams] = useSearchParams();
  const viewScope = params.get('scope') ?? (activeStore ? 'effective' : 'all');
  const items = rows.filter((row) => inScope(row, viewScope, activeStore?.id));
  const scopeLabelId = useId();

  const setViewScope = (next: string) => {
    const query = new URLSearchParams(params);
    if (next === (activeStore ? 'effective' : 'all')) query.delete('scope');
    else query.set('scope', next);
    setParams(query);
  };

  const scopeTabs = activeStore
    ? [
        { id: 'effective', label: `共通 ＋ ${activeStore.name}` },
        { id: 'clinic', label: 'クリニック共通' },
        { id: 'store', label: activeStore.name },
      ]
    : [
        { id: 'all', label: '共通 ＋ 全店舗' },
        { id: 'clinic', label: 'クリニック共通' },
      ];

  /** null = closed, 'new' = creating, otherwise the supervisor being edited. */
  const [open, setOpen] = useState<'new' | Supervisor | null>(null);
  /** The one being READ. Reading a credit and rewriting it are different acts. */
  const [viewing, setViewing] = useState<Supervisor | null>(null);
  const [form, setForm] = useState<Form>(EMPTY);
  const [busy, setBusy] = useState(false);

  const editing = open !== 'new' && open !== null ? open : null;
  /*
   * Placeholders, but only while there is genuinely nothing to show.
   *
   * See Skeleton.tsx: a background refresh keeps the tiles on screen, because
   * replacing what somebody is reading with grey boxes throws away what they
   * were reading.
   */
  const pending = usePendingScreen();

  function start(target: 'new' | Supervisor) {
    setForm(target === 'new' ? EMPTY : formFor(target));
    setViewing(null);
    setOpen(target);
  }

  const save = () => {
    const years = form.careerYears.trim();
    const payload = {
      name: form.name,
      title: form.title,
      qualifications: form.qualifications,
      profile: form.profile,
      // The column is an integer and the input is a string; an empty box is the
      // absence of an answer, not a zero-year career.
      ...(years === '' ? {} : { careerYears: Number(years) }),
    };
    if (editing) return patch(`${base}/supervisors/${editing.id}`, payload);
    return post(`${base}/supervisors`, newScope ? { ...payload, storeId: newScope } : payload);
  };

  /**
   * Approve from the sheet, where the decision is made.
   *
   * Takes the row rather than reading `editing`, because the sheet is open and
   * the form is not - and it re-reads afterwards so the pill changes under the
   * press instead of going stale until the dialog closes.
   */
  async function approve(supervisor: Supervisor | null, status: 'APPROVED' | 'DRAFT') {
    if (!supervisor) return;
    setBusy(true);
    try {
      await patch(`${base}/supervisors/${supervisor.id}`, { status });
      await revalidator.revalidate();
      setViewing({ ...supervisor, status });
    } finally {
      setBusy(false);
    }
  }

  async function setStatus(status: 'APPROVED' | 'DRAFT') {
    if (!editing) return;
    setBusy(true);
    try {
      await patch(`${base}/supervisors/${editing.id}`, { status });
      await revalidator.revalidate();
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!editing) return;
    const ok = await confirmDialog({
      title: 'この監修医を削除しますか？',
      message: `「${form.name || editing.name || 'この監修医'}」を写真とともに削除します。元に戻せません。記事の監修表記として選ばれていた場合は、選択が外れます。`,
      confirmLabel: '削除する',
      danger: true,
    });
    if (!ok) return;
    // /permanent - the bare DELETE only retires. See the same note in Ctas.tsx.
    await del(`${base}/supervisors/${editing.id}/permanent`);
    setOpen(null);
    await revalidator.revalidate();
  }

  return (
    <Card
      lead={lead}
      title="監修医"
      caption="記事に載せる監修表記の元データです。承認した監修医だけが記事で選べます。経歴は事実のみを登録してください。"
      /*
       * A doctor's name, title and qualifications are printed on the clinic's
       * own ドクター紹介 page, and the harvester reads supervisors - this screen
       * simply never offered it, on either side. ペルソナ deliberately has no
       * such button: a persona is an imagined reader, not something a website
       * states (see facts/harvest.ts).
       */
      actions={
        <AddFromLink
          resource="supervisors"
          storeId={newScope || undefined}
          {...(clinicId ? { clinicId } : {})}
          onQueued={() => void revalidator.revalidate()}
        />
      }
    >
      {(isClinicAdmin || activeStore) && (
        <div className="scopebar">
          <span className="scopebar__label" id={scopeLabelId}>
            表示
          </span>
          <div className="scopebar__options" role="group" aria-labelledby={scopeLabelId}>
            {scopeTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className="scopeopt"
                aria-pressed={tab.id === viewScope}
                onClick={() => setViewScope(tab.id)}
              >
                {tab.label}
                {tab.id === viewScope && <span className="scopeopt__count">{items.length}</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {pending ? (
        <SkeletonPeople />
      ) : (
        <div className="pgrid">
          <button className="ptile ptile--add" type="button" onClick={() => start('new')}>
            <span className="ptile__plus" aria-hidden>
              ＋
            </span>
            <span className="ptile__label">監修医を追加</span>
            <span className="ptile__desc">
              氏名だけでも登録できます。写真がなければ仮の丸で表示されます。
            </span>
          </button>

          <AnimatePresence mode="popLayout">
            {items.map((supervisor, index) => (
              <SupervisorTile
                key={supervisor.id}
                supervisor={supervisor}
                index={index}
                onOpen={() => setViewing(supervisor)}
              />
            ))}
          </AnimatePresence>
        </div>
      )}

      {items.length === 0 && (
        <p className="muted small" style={{ marginTop: 10, marginBottom: 0 }}>
          {viewScope === 'clinic'
            ? 'クリニック共通の監修医はまだいません。'
            : viewScope === 'store'
              ? 'この店舗だけの監修医はまだいません。共通のものは「クリニック共通」で確認できます。'
              : 'まだ監修医がいません。'}
        </p>
      )}

      <Modal
        open={viewing !== null}
        onClose={() => setViewing(null)}
        title="監修医"
        caption="記事にはこのカードの形で入ります。表示されている内容がそのまま公開されます。"
        wide
      >
        {viewing && (
          <>
            <div className="chips" style={{ marginBottom: 10 }}>
              {viewing.storeId === null ? (
                <Pill>クリニック共通</Pill>
              ) : (
                <Pill tone="muted">{viewing.storeName ?? '店舗'}</Pill>
              )}
              <StatusPill status={viewing.status} />
            </div>
            <SupervisorCard supervisor={viewing} />
          </>
        )}
        <div className="editpanel__actions">
          <button className="btn btn--sm" type="button" onClick={() => viewing && start(viewing)}>
            編集する
          </button>
          {viewing?.status !== 'APPROVED' ? (
            <button
              className="btn btn--ghost btn--sm"
              type="button"
              disabled={busy}
              onClick={() => void approve(viewing, 'APPROVED')}
            >
              承認する
            </button>
          ) : (
            <button
              className="btn btn--ghost btn--sm"
              type="button"
              disabled={busy}
              onClick={() => void approve(viewing, 'DRAFT')}
            >
              下書きに戻す
            </button>
          )}
          {viewing?.status !== 'APPROVED' && (
            <span className="muted small">承認するまで、記事の監修医として選べません。</span>
          )}
        </div>
      </Modal>

      <ModalForm
        open={open !== null}
        onClose={() => setOpen(null)}
        title={editing ? '監修医を編集' : '監修医を追加'}
        caption={
          editing
            ? '保存しても状態は変わりません。承認は監修医を開いたところから行います。'
            : '登録した内容は下書きです。承認するまで記事には使われません。'
        }
        wide
        onSubmit={save}
        submitLabel={editing ? '保存する' : '作成する'}
      >
        <FieldGroup title="この医師はだれか" note="記事の監修表記にそのまま引用されます" />

        {open === 'new' &&
          (isClinicAdmin ? (
            <Field
              label="登録先"
              htmlFor="s-scope"
              span="full"
              hint="クリニック共通にすると、すべての店舗の記事で選べます。店舗を選ぶと、その店舗だけの監修医になります。"
            >
              <Select
                id="s-scope"
                value={newScope}
                onChange={setNewScope}
                options={[
                  { value: '', label: 'クリニック共通（全店舗で使用）' },
                  ...ownStores.map((store) => ({ value: store.id, label: `${store.name}のみ` })),
                ]}
              />
            </Field>
          ) : (
            <p className="notice" role="status">
              登録先：
              <strong>
                {ownStores.find((store) => store.id === newScope)?.name ?? '担当店舗'}
              </strong>
              の監修医として登録されます。クリニック共通の監修医はクリニック管理者が登録します。
            </p>
          ))}

        <Field label="氏名" htmlFor="s-name" required span="wide" hint="例：山田 太郎">
          <input
            id="s-name"
            value={form.name}
            maxLength={100}
            required
            placeholder="山田 太郎"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </Field>

        <Field
          label="経験年数"
          htmlFor="s-years"
          hint="美容医療の経験年数。空欄なら記事にも書きません。"
        >
          <input
            id="s-years"
            type="number"
            min={0}
            max={80}
            value={form.careerYears}
            onChange={(e) => setForm({ ...form, careerYears: e.target.value })}
          />
        </Field>

        <Field label="肩書" htmlFor="s-title" span="wide" hint="例：院長／美容皮膚科医">
          <input
            id="s-title"
            value={form.title}
            maxLength={100}
            placeholder="美容皮膚科医"
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
        </Field>

        <Field
          label="資格・所属学会"
          htmlFor="s-quals"
          span="full"
          hint="記事の監修表記に引用されます。学会名や認定名は正式名称で。"
        >
          <Textarea
            id="s-quals"
            minRows={3}
            value={form.qualifications}
            maxLength={1000}
            placeholder="日本美容皮膚科学会 会員"
            onChange={(e) => setForm({ ...form, qualifications: e.target.value })}
          />
        </Field>

        <Field
          label="プロフィール"
          htmlFor="s-profile"
          span="full"
          hint="専門分野や診療の考え方。どんな医師かが分かるほど、記事の語り口がこの医院のものになります。"
        >
          <Textarea
            id="s-profile"
            minRows={5}
            value={form.profile}
            maxLength={2000}
            onChange={(e) => setForm({ ...form, profile: e.target.value })}
          />
        </Field>

        {editing && (
          <>
            <FieldGroup title="顔写真" note="一覧のカードと、記事の監修カードに表示されます" />
            <Field
              label="画像"
              htmlFor="s-photo"
              span="full"
              hint="登録がなければ、氏名の一文字を入れた丸が代わりに表示されます。"
            >
              <ImageUpload
                imageId={typeof editing.photoId === 'string' ? editing.photoId : null}
                endpoint={`${base}/supervisors/${editing.id}/photo`}
                label="写真を選ぶ"
                placeholder={text(editing.name).charAt(0) || '医'}
                onDone={() => revalidator.revalidate()}
              />
            </Field>

            <div className="field field--full">
              <FieldGroup title="この監修医の状態" note="承認した監修医だけが記事で選べます" />
              <p className="muted small" style={{ margin: '0 0 8px' }}>
                現在: <StatusPill status={editing.status} />
              </p>
              <div className="chips">
                {editing.status !== 'APPROVED' ? (
                  <button
                    className="btn btn--sm"
                    type="button"
                    disabled={busy}
                    onClick={() => void setStatus('APPROVED')}
                  >
                    承認する
                  </button>
                ) : (
                  <button
                    className="btn btn--sm btn--ghost"
                    type="button"
                    disabled={busy}
                    onClick={() => void setStatus('DRAFT')}
                  >
                    下書きに戻す
                  </button>
                )}
                <button
                  className="btn btn--ghost btn--sm"
                  type="button"
                  onClick={() => void remove()}
                >
                  この監修医を削除
                </button>
              </div>
            </div>
          </>
        )}
      </ModalForm>
    </Card>
  );
}
